// BAND box overrides (admin "Box Overrides", type Band): the units of one SKU
// ship strapped together as a single package, no box. Admin marks every order
// item in that package with box "BAND-<override id> / #OVR<n>"; the shipment's
// own `box` stays "Manufacturer Box" and carries the combined (stacked) dims.
// This derives the packer indicator from those order items, so no API or
// schema change is needed.

export const BAND_LABEL = /^\s*BAND-(\d+)\s*\/\s*#/i;

export function isBandBox(box) {
  return typeof box === "string" && BAND_LABEL.test(box);
}

/**
 * @param {{orderItems?: Array<{sku?: string, quantity?: number|string, box?: string}>}} shipment
 * @returns {null|{overrideId: number, units: number, skus: Array<{sku: string, quantity: number}>, label: string}}
 */
export function bandInfo(shipment) {
  const items = (
    shipment && Array.isArray(shipment.orderItems) ? shipment.orderItems : []
  ).filter((item) => item && isBandBox(item.box));
  if (items.length === 0) {
    return null;
  }

  const bySku = new Map();
  for (const item of items) {
    const sku = String(item.sku || "").toUpperCase();
    bySku.set(sku, (bySku.get(sku) || 0) + (parseInt(item.quantity, 10) || 0));
  }

  const skus = [...bySku.entries()].map(([sku, quantity]) => ({
    sku,
    quantity,
  }));
  const units = skus.reduce((sum, s) => sum + s.quantity, 0);

  return {
    overrideId: parseInt(items[0].box.match(BAND_LABEL)[1], 10),
    units,
    skus,
    label:
      "BAND: band " +
      skus.map((s) => s.quantity + " \u00d7 " + s.sku).join(" + ") +
      " together",
  };
}

/** "30 x 20 x 24 inches" from the shipment's (already stacked) dims, or "". */
export function bandDimensions(shipment) {
  if (!shipment || !shipment.length || !shipment.width || !shipment.height) {
    return "";
  }
  const f = (n) => String(parseFloat(Number(n).toFixed(2)));
  return (
    f(shipment.length) +
    " x " +
    f(shipment.width) +
    " x " +
    f(shipment.height) +
    " " +
    (shipment.sizeUnit || "")
  );
}

/**
 * Warning text to confirm before splitting, or null when no warning is needed.
 * Warns when the shipment or the items being split off contain BAND items.
 *
 * @param {object} shipment the shipment being split
 * @param {Array<object>} items the order items being split off
 * @returns {string|null}
 */
export function splitBandWarning(shipment, items = []) {
  const band =
    bandInfo(shipment) ||
    bandInfo({ orderItems: Array.isArray(items) ? items : [] });
  if (!band) {
    return null;
  }

  const what = band.skus
    .map((s) => s.quantity + " \u00d7 " + s.sku)
    .join(" + ");

  return (
    "These items are banded together (BAND " +
    what +
    "). Splitting will break the band. The split-off part will keep the BAND label. Continue?"
  );
}
